import {
    Exercise,
    ExerciseAttachments,
    ExerciseTableData,
    FailedExercise,
    PlanExercise,
    PlanExerciseTableData,
    Stage
} from "../../../../types/domain";
import holidayData from "../../../../assets/holidays.json"
import Holidays from "date-holidays";
import {easter, EasterDate} from "date-easter";
import {DateValue} from "@mantine/dates";

type HolidayType = {
    date: Date,
    name: string
}

/**
 * Sort stages - Prophylaxe is printed as last stage
 * @param name1 name of a stage
 * @param name2 name of a stage
 */
export function sortStages(name1: string, name2: string) {
    let aNum = -1;
    let bNum = -1;
    if (name1 !== "Prophylaxe") {
        aNum = parseInt(name1)
    }
    if (name2 !== "Propyhlaxe") {
        bNum = parseInt(name2)
    }

    return aNum - bNum;
}

/**
 * Format stage names into a compact string, collapsing consecutive numeric runs into ranges
 * @param stages - stages to format and collapse
 */
export function formatStages(stages: Array<Stage>) {
    const names = stages.map(s => s.name);

    const hasProphylaxe = names.includes("Prophylaxe");
    const numbers = names
        .filter(n => n !== "Prophylaxe")
        .map(n => parseInt(n))
        .filter(n => !isNaN(n))
        .sort((a, b) => a - b);

    const ranges: string[] = [];
    let start: number | null = null;
    let prev: number | null = null;

    for (const num of numbers) {
        if (start === null) {
            start = num;
            prev = num;
        } else if (num === prev! + 1) {
            prev = num;
        } else {
            ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
            start = num;
            prev = num;
        }
    }
    if (start !== null) {
        ranges.push(start === prev ? `${start}` : `${start}-${prev}`);
    }

    return [hasProphylaxe ? "Prophylaxe" : "", ...ranges]
        .filter(s => s !== "")
        .join(', ');
}

/**
 * Generate ExerciseTableData array from the array of exercises
 * @param exercises list of exercises
 */
export function generateExerciseTableData(exercises: Array<Exercise>) {
    let tableData: ExerciseTableData[] = [];
    exercises.forEach(exercise => {
        tableData.push({
            id: exercise.id,
            name: exercise.name, description: exercise.description,
            material: exercise.material, method: exercise.methods.map(m => m.code).join(', '),
            stages: formatStages(exercise.stages)
        });
    })
    return tableData
}

/**
 * Generate PlanExerciseTableData array from the array of exercises
 * @param planExercises list of PlanExercises
 * @param newAttachments array of attachments that have been newly uploaded
 * @param planStages plan stages
 */
export function generatePlanExerciseTableData(planExercises: Array<PlanExercise> | null, newAttachments: Array<ExerciseAttachments>, planStages: Array<Stage> = []) {
    let tableData: PlanExerciseTableData[] = [];
    planExercises?.forEach(planExe => {
        const attachmentExe = newAttachments.find(e => e.exercise.name === planExe.exercise.name
            && e.exercise.description === planExe.exercise.description && e.exercise.material === planExe.exercise.material);
        let attachCount: number = 0;
        let paths: string[] = [];

        // Collect DB attachment paths from the PlanExercise's own attachments
        if (planExe.attachments.length !== 0) {
            planExe.attachments.forEach(a => {
                if (a.path && a.path.includes("/")) paths.push(a.path.replace('"', ""));
                if (a.solutionPath && a.solutionPath.includes("/")) paths.push(a.solutionPath.replace('"', ""));
            });
            paths = paths.filter(p => p !== "");
        }

        // Add any newly queued attachments on top
        if (attachmentExe) {
            const solutionCount = attachmentExe.attachments.filter(a =>
                a.type.name === 'Arbeitsblatt' && a.solutionFile !== null
            ).length;
            attachCount = paths.length + attachmentExe.attachments.length + solutionCount;
        } else {
            attachCount = paths.length;
        }

        tableData.push({
            id: planExe.exercise.id,
            name: planExe.exercise.name, description: planExe.exercise.description,
            material: planExe.exercise.material, method: planExe.exercise.methods.map(m => m.code).join(', '),
            execution: planExe.execution ? planExe.execution : "",
            interest: planExe.interest ?? 0,
            attachments: attachCount,
            stages: planExe.exercise.stages.length > 0 ? formatStages(planExe.exercise.stages) : formatStages(planStages),
            paths: paths
        });
    })
    return tableData
}

/**
 * Sort the array of PlanExercises by the position attribute
 * @param planExercises list of PlanExercises
 */
export function sortPlanExercisesByPosition(planExercises: Array<PlanExercise>) {
    return planExercises.sort((a: PlanExercise, b: PlanExercise) => {
        if (a.position && b.position) {
            return a.position - b.position;
        }
        return 0;
    })
}

/**
 * Get holidays and season around the training date
 * @param trainingDate date of the planned training
 */
export function getHolidaysAndSeason(trainingDate: string) {
    let trainDate = new Date(trainingDate);
    trainDate.setTime(trainDate.getTime() - (60 * 60 * 1000))  //set date to 00:00

    let twoWeeks = new Date(trainingDate);
    twoWeeks.setDate(twoWeeks.getDate() + 14);

    let holidays: HolidayType[] = [];

    // custom fixed holidays
    holidayData.holidays.forEach((holiday) => {
        let tokens = holiday.date.split('.')
        let date = new Date(new Date().getFullYear(), parseInt(tokens[1]) - 1, parseInt(tokens[0]))

        if (date.getTime() >= trainDate.getTime() && date.getTime() <= twoWeeks.getTime()) {
            holidays.push({
                "date": date,
                "name": holiday.name
            })
        }
    })

    //custom seasons
    holidayData.seasons.forEach((season) => {
        let tokensStart = season.start.split('.')
        let tokensEnd = season.end.split('.')

        let start = new Date();
        if (parseInt(tokensStart[1]) > parseInt(tokensEnd[1])) {
            start = new Date(new Date().getFullYear() - 1, parseInt(tokensStart[1]) - 1, parseInt(tokensStart[0]))
        } else {
            start = new Date(new Date().getFullYear(), parseInt(tokensStart[1]) - 1, parseInt(tokensStart[0]))
        }

        let end = new Date(new Date().getFullYear(), parseInt(tokensEnd[1]) - 1, parseInt(tokensEnd[0]))

        if ((trainDate.getTime() >= start.getTime() && trainDate.getTime() <= end.getTime()) ||
            (twoWeeks.getTime() >= start.getTime() && twoWeeks.getTime() <= end.getTime())) {
            holidays.push({
                "date": end,
                "name": season.name
            })
        }
    })

    // general austrian holidays
    let hd = new Holidays("AT");
    hd.getHolidays(new Date().getFullYear()).forEach(holiday => {
        let date = new Date(holiday.date);
        if (date.getTime() >= trainDate.getTime() && date.getTime() <= twoWeeks.getTime()) {
            holidays.push({
                "date": date,
                "name": holiday.name.toUpperCase()
            })
        }
    })

    // calculable holidays
    const calcHolidays = getCalculableHolidays(trainDate);
    calcHolidays.forEach((holiday) => {
        if (holiday.date.getTime() >= trainDate.getTime() && holiday.date.getTime() <= twoWeeks.getTime()) {
            holidays.push(holiday);
        }
    })

    //sort by date and return only names
    return holidays.sort((h1: HolidayType, h2: HolidayType) => {
        return h1.date.getTime() - h2.date.getTime()
    }).map(h => h.name)
}

/**
 * Get list of holidays that need to be calculated according to year
 * of the training date
 * @param trainDate date, where the training should be held
 */
function getCalculableHolidays(trainDate: Date) {
    let holidays: HolidayType[] = [];

    //ash wednesday, carnival tuesday and rose monday
    const easterDate: EasterDate = easter(trainDate.getFullYear());
    let ashWednesday = new Date(easterDate.year, easterDate.month - 1, easterDate.day);
    ashWednesday.setDate(ashWednesday.getDate() - 46);  //always 46 days after


    holidays.push({
        "date": ashWednesday,
        "name": "ASCHERMITTWOCH"
    });


    let carnivalTuesday = new Date(ashWednesday.getTime()); //now carnival tuesday
    carnivalTuesday.setDate(carnivalTuesday.getDate() - 1); //now carnival tuesday
    holidays.push({
        "date": carnivalTuesday,
        "name": "FASCHINGDIENSTAG"
    });

    let roseMonday = new Date(carnivalTuesday.getTime()); //now carnival tuesday
    roseMonday.setDate(roseMonday.getDate() - 1); //now carnival tuesday
    holidays.push({
        "date": roseMonday,
        "name": "ROSENMONTAG"
    })
    return holidays;
}

/**
 * Gets the filename and extension without timestamp
 * @param path of the file
 */
export function getFileNameFromPath(path: string): string {
    const parts = path.replace(/\\/g, "/").split("/");
    let filename = parts.pop() || "";

    // Remove timestamp
    filename = filename.replace(/-\d{6}_\d{6}(?=\.[^.]+$)/, "");

    return filename;
}

/**
 * Date converter to a backend readable date format
 * @param date date value to format
 */
export function convertDateFormat(date: DateValue): string {
    if (date) {
        const dateObj = new Date(date);

        const day = String(dateObj.getDate()).padStart(2, '0'); // Day with leading zero
        const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so +1
        const year = dateObj.getFullYear();

        // Custom format: yyyy-MM-dd
        return `${year}-${month}-${day}`;
    }
    return "";
}

/**
 * Returns a unique filename by appending _1, _2, ... before the extension
 * @param original original filename
 * @param usedNames set with already used names
 * @param baseCounts map that keeps track how often which filename was already Sused
 */
export function getUniqueFilename(original: string, usedNames: Set<string>, baseCounts: Map<string, number>): string {
    // Split into base name + extension
    const dotIndex = original.lastIndexOf(".");
    const name = dotIndex > 0 ? original.substring(0, dotIndex) : original;
    const ext = dotIndex > 0 ? original.substring(dotIndex) : "";
    const base = name;

    // Init the counter, if not present
    if (!usedNames.has(original)) {
        if (!baseCounts.has(base)) baseCounts.set(base, 0);
        return original;
    }

    // Find next free numbered variant
    let n = (baseCounts.get(base) ?? 0) + 1;
    let candidate = `${base}_${n}${ext}`;
    while (usedNames.has(candidate)) {
        n++;
        candidate = `${base}_${n}${ext}`;
    }

    baseCounts.set(base, n);
    return candidate;
}

/**
 * Calculate the total size of all given attachments
 * @param attachments list of attachments
 */
export function getTotalAttachmentSize(attachments: ExerciseAttachments[]): number {
    return attachments.reduce((total, exeAttachment) =>
            total + exeAttachment.attachments
                .reduce((sum, a) => sum + (a.file?.size ?? 0), 0)
        , 0);
}

/**
 * Returns not failed plan exercises (because sometimes planExercises contains failed ones)
 * @param planExercises
 * @param failedExercises
 */
export function planExercisesNotInFailedExercises(planExercises: PlanExercise[], failedExercises: FailedExercise[]): PlanExercise[] {
    let notFailed: PlanExercise[] = [];
    const failedIds = failedExercises.map(fe => fe.exercise.id);

    // only add plan exercise to notFailed if it is not part of failedExercises
    for (const pe of planExercises) {
        const exercise = pe.exercise;
        // compare name, description and material for not saved exercises
        if (exercise.id === 0) {
            if (!failedExercises.some(fe => fe.exercise.name === exercise.name && fe.exercise.description === exercise.description && fe.exercise.material === exercise.material)) {
                notFailed.push(pe);
            }
        } else {
            // compare ids for already saved exercises
            if (!failedIds.includes(exercise.id)) {
                notFailed.push(pe);
            }
        }
    }
    return notFailed;
}

export function getAttachmentPaths(exercises: Array<PlanExercise>) {
    let attachments: string[] = [];
    // get paths of all attachments and solution sheets
    exercises?.forEach(planExe => {
        planExe.attachments.forEach(attach => {
            attachments.push(attach.path);
            if(attach.solutionPath != '')
                attachments.push(attach.solutionPath);
        })
    })
    return attachments;
}